
import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceCard } from "@/components/ServiceCard";
import { useFavorites } from "@/hooks/useFavorites";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ProfileSidebar } from "@/components/profile/ProfileSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";

interface FavoriteService {
  id: number;
  name: string;
  subcategory_details: {
    label: string | null;
    parent: {
      label: string | null;
    } | null;
  } | null;
  price: number | null;
  rating: number | null;
  image_url: string;
  location: string | null;
  phone: string | null;
  email: string | null;
  published: boolean;
  region: string;
}

const Favorites = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("nairobi");
  const { favorites, loading: favoritesLoading, refreshFavorites } = useFavorites();
  const [favoriteServices, setFavoriteServices] = useState<FavoriteService[]>([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      fetchFavoriteServices();
    };

    checkAuth();
  }, [favorites]);

  const fetchFavoriteServices = async () => {
    if (favorites.length === 0) {
      setFavoriteServices([]);
      setLoading(false);
      return;
    }

    try {
      const serviceIds = favorites.map(fav => fav.service_id);
      
      const { data, error } = await supabase
        .from('services')
        .select(`
          *,
          subcategory_details:subcategories(
            *,
            parent:categories(*)
          )
        `)
        .in('id', serviceIds)
        .eq('published', true); // Only show published services

      if (error) throw error;

      // Prefetch reviews for all favorite services
      if (data && data.length > 0) {
        for (const service of data) {
          queryClient.prefetchQuery({
            queryKey: ['reviews', service.id],
            queryFn: () => null, // The actual fetch will be done by the hook
            staleTime: 1000 * 60 * 5, // 5 minutes
          });
        }
      }

      setFavoriteServices(data as FavoriteService[] || []);
    } catch (error) {
      console.error('Error fetching favorite services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        {/* Fixed header */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16">
          <Header
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
        
        {/* Main layout with sidebar - starts after header */}
        <div className="flex flex-1 pt-16">
          <ProfileSidebar />
          
          <SidebarInset>
            {/* Main content with proper spacing to clear header */}
            <main className="flex-1 overflow-auto p-4 md:p-6">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-4">My Favorites</h1>
                
                {loading || favoritesLoading ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : favoriteServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {favoriteServices.map(service => (
                      <ServiceCard
                        key={service.id}
                        id={service.id}
                        title={service.name || ""}
                        subcategory_details={service.subcategory_details}
                        price={service.price}
                        rating={service.rating}
                        image={service.image_url || ""}
                        contact={{
                          location: service.location,
                          phone: service.phone,
                          email: service.email
                        }}
                        region={service.region || "nairobi"}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-center text-muted-foreground">You don't have any favorites yet.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </main>
          </SidebarInset>
        </div>
        
        <Footer />
      </div>
    </SidebarProvider>
  );
};

export default Favorites;
